import { createSlice } from "@reduxjs/toolkit";
import { showNotification } from "../../components/Notification";
import {
  createTemplate,
  deleteTemplate,
  getAllTemplates,
  getTemplateDetailsById,
  getTemplatesByDeptId,
  getTemplatesByUserId,
} from "../../api/templateApi";
import { updateUserAssignedAppraisal } from "../../api/appraisalApi";

const initialState = {
  templates: [],
  templateById: null,
  templatesByUserId: null,
  loading: false,
  status: "idle", //'idle' | 'loading' | 'succeeded' | 'failed',
  success: false,
};

const templateSlice = createSlice({
  name: "templateState",
  initialState,
  reducers: {
    updateTemplate(state, action) {
      state.templates = state.templates.map((template) =>
        template.id === action.payload.id ? action.payload : template
      );
    },
    resetTemplateState(state) {
      state.loading = false;
      state.status = "";
      state.success = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTemplates.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTemplates.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(getAllTemplates.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload || "Failed to fetch templates";
        showNotification("error", action.payload?.message);
      })

      .addCase(getTemplatesByDeptId.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(getTemplatesByDeptId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(getTemplatesByDeptId.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload || "Failed to fetch templates";
        showNotification("error", action.payload?.message);
      })
      
      .addCase(getTemplateDetailsById.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(getTemplateDetailsById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.templateById = action.payload;
        state.error = null;
      })
      .addCase(getTemplateDetailsById.rejected, (state, action) => {
        state.status = "loading";
        state.loading = false;
        state.error = action.payload?.message;
      })
      .addCase(createTemplate.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        if (
          action.payload.editId === null ||
          action.payload.editId === undefined
        ) {
          state.templates = state.templates.concat(action.payload?.data);
          showNotification(
            "success",
            "The template has been successfully created."
          );
        } else {
          state.templates = state.templates?.map((template) =>
            template?.id === action.payload.data.id
              ? action.payload.data
              : template
          );
          showNotification(
            "success",
            "The template has been successfully updated."
          );
        }
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
        state.loading = false;
        showNotification("error", action.payload?.message);
      })
      .addCase(deleteTemplate.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.templates = state.templates?.filter(
          (template) => template?.id !== action.payload
        );
        showNotification(
          "success",
          "The template has been successfully deleted."
        );
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload;
        showNotification("error", "You cannot delete the template once someone is viewing it or has made any changes.");
      })
      .addCase(getTemplatesByUserId.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(getTemplatesByUserId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.templatesByUserId = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(getTemplatesByUserId.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
      })
      .addCase(updateUserAssignedAppraisal.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateUserAssignedAppraisal.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.error = null;
        state.success = true;
        showNotification(
          "success",
          "The appraisal has been successfully updated."
        );
      })
      .addCase(updateUserAssignedAppraisal.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
        state.success = false;
        showNotification("error", action.payload?.message);
      });
  },
});

export const { updateTemplate, resetTemplateState } = templateSlice.actions;

export const templateState = (state) => state.templateState;

export const getTemplates = (state) => state?.templateState?.templates;
export const getTemplate = (state) => state?.templateState?.templateById;
export const getTemplatesOfUser = (state) =>
  state?.templateState?.templatesByUserId;

export const selectTemplatesOfUserByTemplateId = (state, templateId) => {
  const templates = Array.isArray(state?.templateState?.templatesByUserId)
    ? state.templateState.templatesByUserId
    : [];
  return templates.find((template) => template?.id === templateId) || null;
};
export const selectTemplateById = (state, templateId) => {
  const templates = Array.isArray(state?.templateState?.templates)
    ? state.templateState.templates
    : [];
  return templates.find((template) => template?.id === templateId) || null;
};

export default templateSlice.reducer;
